import React, { useEffect, useState, useCallback, Fragment } from "react";
// https://github.com/facebook/react/issues/10780
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents, LayersControl, LayerGroup, Circle, CircleMarker } from "react-leaflet";
import L from 'leaflet'
import { useAppContext } from "./helpers/account-context";

import { ICONS, processLocations, sortNearbyLocations } from "./helpers/helper";
import { lookupLocations, CBAnalytics } from "./helpers/apiEndpoints";
import {
  requestGeocode,
  convertJsonToParams,
  syncLocationStates,
  fetchJSON
} from "./helpers/sharedUtils";
import { GMarker, ClusterMarker, GeoMarker, InfoWindow } from "./Maps/Gmaps";
import { ListResults } from "./Maps/Results";
import LocationDialog from "./Maps/LocationDialog";
import FilterDialog from "./Maps/FilterDialog";
import SearchOuter from "./Maps/SearchOuter";
import SearchInner from "./Maps/SearchInner";
import LeadForm from "./Maps/LeadForm";
import { Loading } from "./Maps/Components/Mixin";
import TaggableFilters from "./Maps/TaggableFilters";
import { useMixMap } from "./helpers/useMixMap";

const CLUSTER_MAX_ZOOM = 14;

const isInBounds = (bounds, { properties: { lat, lng } }) => {
  return (
    lng >= bounds.nw_lng &&
    lng <= bounds.se_lng &&
    lat >= bounds.nw_lat &&
    lat <= bounds.se_lat
  );
};

const detectPointsInBounds = ({ bounds }, points) => {
  if (!bounds) return false;
  return points.find((point) => isInBounds(bounds, point));
};

const appendClassName = (account, initedData) => {
  const {
    mapTheme,
    filterLayout,
    tagsLayout,
    filtersPosition,
    contentPositionDesktop,
    contentPositionMobile,
  } = initedData;
  const themePrefix = "cbmap-theme";
  return `cbmap-widget
    ${themePrefix}-${mapTheme || account.map_theme || "default"} 
    ${themePrefix}_filters-layout-${filterLayout || "insiderow"}
    ${themePrefix}_tags-layout-${tagsLayout || "dropdown"}
    ${themePrefix}_filters-position-${filtersPosition || "inside"}
    ${themePrefix}_content-position_d-${contentPositionDesktop || "left"}
    ${themePrefix}_content-position_m-${contentPositionMobile || "top"}
  `;
};

const timerFunc = (cb, seconds) => {
  if (seconds === 0) return cb();
  setTimeout(() => {
    cb();
  }, seconds * 1000);
};

class OsmGeocoder {
  constructor(mapbox_api_key) {
    this.mapbox_api_key = mapbox_api_key;
  }
  geocode = (opts) => {

    let query = opts.address || opts.place_id;
    if (opts.location) {
      query = `${opts.location.lat},${opts.location.lng}`;
    }
    console.log('geocode', query);
    return new Promise((resolve, reject) => {

      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${query}`;
      fetchJSON(url)
        .then((resp) => {
        const predictions = resp.map((item) => ({
          ...item,
          place_id: item.place_id,
          description: item.name,
          isOSM: true,
          formatted_address: item.display_name,
          geometry: {
            ...item.geometry,
            location: {
              lng: +item.lon,
              lat: +item.lat,
            },
          },
        }));
        resolve({ results: predictions });
        })
        .catch((err) => {
          console.error('error', err);
          reject(err);
        });
    });
  };
  
  getPlacePredictions = (req, cb) => {

    const params = {
      q: req.input,
      format: 'json',
    }

    if(req.componentRestrictions && req.componentRestrictions.country && req.componentRestrictions.country.length > 0) {
      params.countrycodes = req.componentRestrictions.country.join(',');
    }

    const url = `https://nominatim.openstreetmap.org/search?${convertJsonToParams(params)}`;
    fetchJSON(url)
      .then((resp) => {
      const predictions = resp.map((item) => ({
        ...item,
        place_id: item.place_id,
        description: item.display_name,
        isOSM: true,
        geometry: {
          coordinates: [+item.lon, +item.lat]
        }
      }));
      cb(predictions);
    })
   
  };
}
const MapEventHooks = ({events}) =>{
  const map = useMapEvents(events);
  return null
}

const MapBox = (props) => {
  const {
    account,
    initedData = {},
    currentGeo,
    setCurrentGeo,
    getLocale,
  } = useAppContext();
  const {
    clusterEnabled,
    clusterSettings,
    defaultCenter,
    currentParams,
    containerRef,
    contentRef,
    mapViewRef,
    mapRef,
    geocoder,
    setGeocoder,
    filters,
    setFilters,
    isLoading,
    setIsLoading,
    isCollapsed,
    setIsCollapsed,
    openingFilters,
    setOpeningFilters,
    mapViewPort,
    setMapViewPort,
    selectedLocation,
    setSelectedLocation,
    hoveredLocation,
    setHoveredLocation,
    points,
    setPoints,
    clusters,
    supercluster,
  } = useMixMap({});

  const { geo_type, enable_custom_params, mapbox_api_key, search_restriction } = account;
  const popupRefs = {};
  const zoomAndMoveMap = useCallback(
    (opts) => {
      
      timerFunc(
        () => {
          console.log('zoomAndMoveMap', opts);
          mapRef.current.flyTo(
            [opts.lat, opts.lng],
            (opts.zoom || mapViewPort.zoom || 10),
            {
              duration: 0.5,
            }
          );
        },
        opts.zoom ? 0.2 : 0
      );
    },
    [mapViewPort.zoom]
  );

  const forceToCenter = useCallback(
    (opts = {}) => {
      if (mapRef && mapRef.current && opts.lat && opts.lng) {
        zoomAndMoveMap(opts);
      } else {
        timerFunc(() => {
          if (mapRef.current) {
            zoomAndMoveMap(opts);
          }
        }, 2);
      }
    },
    [zoomAndMoveMap]
  );

  const doFilters = useCallback(
    async (params, cb = null) => {
      setIsLoading(true);
      lookupLocations(account.uid, params)
        .then(({ resources }) => {
          const newPoints = processLocations(resources);
          // console.log("doFilters", params,);
          if (params && params.geo && params.geo.lat && params.geo.lng) {      
            const locDistanceSorted = sortNearbyLocations(newPoints, params.geo.lat, params.geo.lng, false); 
            console.log('locDistanceSorted', locDistanceSorted);
            setPoints(locDistanceSorted);
          } else {
            setPoints(newPoints);          
           }

          if (!filters.isFiltered) {
            setFilters({ ...filters, ...params, isFiltered: 1 });
          }
          setIsLoading(false);
          const isInside = detectPointsInBounds(mapViewPort, newPoints);
          if (!isInside && newPoints.length > 0) {
            forceToCenter({
              lat: +newPoints[0].properties.lat,
              lng: +newPoints[0].properties.lng,
              zoom: 6,
            });
          } else {
            forceToCenter({
              lat: +currentGeo.lat,
              lng: +currentGeo.lng,
              zoom: 6,
            });
          }
          setTimeout(() => {
            if (mapRef.current) {
              mapRef.current.invalidateSize();
            }
          }, 200);
          if (cb) cb(resources);
        })
        .catch((err) => {
          console.log("error", err);
          setIsLoading(false);
        });
    },
    [account, mapViewPort, filters, forceToCenter]
  );

  useEffect(() => {
    const defaultParams = {};
    let initFiltering = true;
    if (!!initedData.selectedLocation) {
      defaultParams.location = initedData.selectedLocation;
    }
    if (!!currentParams.location) {
      defaultParams.location = currentParams.location;
    } else if (geo_type === "geo_browser_ip") {
      defaultParams.geo = filters.geo;
    } else if (geo_type === "disable") {
      defaultParams.all = 1;
    } else if (geo_type === "typing") {
      initFiltering = false;
    }
    if (!!initedData.selectedLocation || initFiltering) {
      console.log('call effect');
      doFilters({ ...defaultParams }, (resources) => {
        const loc = resources.find(
          (loc) =>
            loc.uid === currentParams.location ||
            loc.slug === currentParams.location
        );
        if (loc) {
          setFilters({ ...filters, isFiltered: true, search: loc.name });
        } else if (geo_type === "geo_browser_ip") {
          setFilters({
            ...filters,
            isFiltered: true,
            search: currentGeo.address,
          });
        }
      });
    }
  }, []);

  const initGeoData = async (newGeocoder) => {
    if (!currentParams.search || currentParams.search.length < 3) {
      if (geo_type === "geo_browser_ip" && !currentGeo.address) {
        const currentLocation = await requestGeocode(newGeocoder, {
          location: { lat: currentGeo.lat, lng: currentGeo.lng },
        });
        console.log("currentLocation", currentLocation);
        if (currentLocation.results.length > 0) {
          setFilters({
            ...filters,
            search: currentLocation.results[0].place_name,
          });
          setCurrentGeo({
            ...currentGeo,
            address: currentLocation.results[0].place_name,
          });
        }
      }
      return null;
    }

    const { results } = await requestGeocode(newGeocoder, {
      address: currentParams.search,
    });
    if (!results || results.length === 0) return null;
    setFilters({ ...filters, search: currentParams.search });

    const searchedLoc = results[0].geometry.location;
    const { lat, lng } = searchedLoc;
    const geo =
      typeof lat === "function" ? { lat: lat(), lng: lng() } : { lat, lng };

    const newFilters = {
      ...filters,
      geo: geo,
      search: currentParams.search,
    };

    setFilters({
      ...newFilters,
      isFiltered: true,
    });

    doFilters(newFilters);
    forceToCenter({
      ...geo,
      zoom: 12,
    });
  };

  const handleSelectLocation = useCallback(
    (place) => {
      if (!place) return setSelectedLocation(null);

      setSelectedLocation(place);
     
      forceToCenter({
        lng: place.properties.lng,
        lat: place.properties.lat,
        zoom: Math.max(mapViewPort.zoom, CLUSTER_MAX_ZOOM + 1),
      });

      const { search, ...newParams } = currentParams;
      if (enable_custom_params) {
        syncLocationStates({
          ...newParams,
          location: place.properties.slug,
        });
      }
      const popup = popupRefs[place.properties.uid];
      if (popup && !popup.isOpen()) {
        popup.toggle();
      }
      if (!initedData.preview) {
        CBAnalytics.click(account.uid, {
          location_name: place.properties.name,
          address: place.properties.full_address,
          location_id: place.properties.num,
          clicked_url: window.location.href,
        });
      }
    },
    [forceToCenter, account.uid, currentParams]
  );

  const handleHoverLocation = (place) => {
    console.log('handleHoverLocation', place)
    // setHoveredLocation(place);
  };

  const handleClickCluster = (cluster) => {
    console.log('handleClickCluster', cluster);
    const expansionZoom = Math.min(
      supercluster.getClusterExpansionZoom(cluster.id),
      CLUSTER_MAX_ZOOM + 1
    );

    mapRef.current.flyTo(
      cluster.geometry.coordinates.reverse(),
      expansionZoom,
      {duration: 0.5}
    );
    setMapViewPort({ ...mapViewPort, zoom: expansionZoom });
  };

  const handleOpenFilters = (filterType) => {
    setOpeningFilters(filterType);
  };

  const handleChangeFilters = (ft) => {
    setFilters({ ...ft });
  };

  const handleChangeMapBounds = useCallback(
    (newViewPort) => {
      if (!mapRef.current) return false;
      const aBounds = mapRef.current.getBounds();

      const newBounds = {
        nw_lng: aBounds.getSouthWest().lng,
        se_lat: aBounds.getSouthWest().lat,
        se_lng: aBounds.getNorthEast().lng,
        nw_lat: aBounds.getNorthEast().lat,
      };
      setMapViewPort({
        ...mapViewPort,
        zoom: mapRef.current.getZoom(),
        bounds: newBounds,
      });

      setFilters({
        ...filters,
        bounds: { ...newBounds },
      });
    },
    [filters, mapViewPort]
  );

  const handleSelectSearch = (data) => {
    const { lat, lng, location, ...existingFilters } = filters;
    const newFilters = {
      ...existingFilters,
      search: data.search,
      geo: {
        lat: data.lat ? data.lat : "",
        lng: data.lng ? data.lng : "",
      },
    };
    if (data.search && data.search.length > 0) {
      delete newFilters.country;
      delete newFilters.province;
      delete newFilters.city;
      delete newFilters.all;
    }
    if (data.location) {
      newFilters.location = data.location;
    }

    setFilters({ ...newFilters });
    doFilters({ ...newFilters });
    if (enable_custom_params && !data.lat && !data.lng) {
      const { location, search, ...newParams } = currentParams;
      syncLocationStates({ ...newParams });
    }
  };

  const handleApplyFilters = useCallback(
    (ft) => {
      if (ft) {
        const newFilters = { ...filters, ...ft };
        if (ft && ft.all === 1) {
          delete newFilters.search;
          delete newFilters.geo;
          delete newFilters.location;
        }
        setFilters({ ...newFilters });
        doFilters({ ...newFilters });
      } else {
        doFilters({ ...filters });
      }
      setOpeningFilters(false);
      const { location, ...newParams } = currentParams;
      if (enable_custom_params) {
        syncLocationStates({ ...newParams });
      }
    },
    [currentParams, filters, enable_custom_params, doFilters]
  );

  const initOSM = (newMap) => {
    const aBounds = newMap.getBounds();
    const newBounds = {
      nw_lng: aBounds.getSouthWest().lng,
      se_lat: aBounds.getSouthWest().lat,
      se_lng: aBounds.getNorthEast().lng,
      nw_lat: aBounds.getNorthEast().lat,
    };
    setMapViewPort({
      ...mapViewPort,
      bounds: newBounds,
    });
    const mapboxCoder = new OsmGeocoder(mapbox_api_key);
    setGeocoder(mapboxCoder);
    initGeoData(mapboxCoder);

    doFilters()
  }
 

  const handleToggleContent = (e) => {
    e.preventDefault();
    setIsCollapsed(!isCollapsed);
    if (mapRef.current) {
      setTimeout(() => {
        mapRef.current.invalidateSize();
      }, 200);
    }
  };

  const dialog = account.dialog || {};
  const markers = (true || clusterEnabled) ? clusters : points;
  const showingDetails = dialog.detail_enabled && selectedLocation;
  const showListing = !showingDetails && !openingFilters;
  const isMobile = window.screen.availWidth < 767;
  const mapHeight = isMobile ? 700 : containerRef.current?.clientHeight;
  const maxContentHeight = mapHeight - mapViewRef.current?.clientHeight;
  const defaultImageUrl = "https://cbmap-prod.s3.us-west-1.amazonaws.com/markers/maker-100-new-26.png";
  
  return (
    <div className={appendClassName(account, initedData)}>
      <SearchOuter
        filters={filters}
        onChangeSetting={handleOpenFilters}
        onSelectSearch={handleSelectSearch}
        onChangeFilters={handleChangeFilters}
        onApplyFilters={handleApplyFilters}
        geocoder={geocoder}
        autoSuggestion={geocoder}
      />
      <TaggableFilters
        filters={filters}
        onChangeFilters={handleChangeFilters}
        onApplyFilters={handleApplyFilters}
      />

      <div
        className="cbmap-wrapper"
        ref={containerRef}
        style={{
          height: !isMobile && initedData.widgetHeight ? initedData.widgetHeight : "",
        }}
      >
        <div
          className={`cbmap-content ${isCollapsed ? "is-collapsed" : ""}`}
          ref={contentRef}
          style={{
            height:
              isMobile && (isLoading || openingFilters) ? maxContentHeight : "",
          }}
        >
          <div
            className="cbmap-content-togglebar"
            onClick={handleToggleContent}
            dangerouslySetInnerHTML={{ __html: ICONS.CARET }}
            title={isCollapsed ? getLocale("show") : getLocale("hide")}
          />
          <Loading isLoading={isLoading} />
          <FilterDialog
            isOpen={!isLoading && openingFilters}
            filters={filters}
            filterType={openingFilters}
            onChangeFilters={handleChangeFilters}
            onApplyFilters={handleApplyFilters}
            onClose={(e) => setOpeningFilters(false)}
          />
          {showingDetails && (
            <LocationDialog
              isOpen={!!selectedLocation}
              item={selectedLocation}
              onClose={(e) => setSelectedLocation(null)}
            />
          )}

          <SearchInner
            isOpen={!isLoading && showListing}
            filters={filters}
            onChangeFilters={handleChangeFilters}
            onChangeSetting={handleOpenFilters}
            onSelectSearch={handleSelectSearch}
            onApplyFilters={handleApplyFilters}
            geocoder={geocoder}
            autoSuggestion={geocoder}
          />
          {!isLoading && !showingDetails && !openingFilters && (
            <LeadForm
              filters={filters}
              results={points}
              onApplyFilters={handleApplyFilters}
            />
          )}

          <ListResults
            showListing={!isLoading && showListing}
            selectedLocation={selectedLocation}
            hoveredLocation={hoveredLocation}
            handleSelectLocation={handleSelectLocation}
            handleHoverLocation={handleHoverLocation}
            points={points}
            currentGeo={currentGeo}
          />
        </div>
        <div
          className="cbmap-mapview"
          ref={mapViewRef}
          style={{ height: mapHeight }}
        >
          <MapContainer
            center={[51.505, -0.09]}
            zoom={13}
            style={{ height: `${mapHeight || 400}px`, minHeight: '100%' }}
            ref={mapRef}
            whenReady={e => initOSM(e.target)}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {markers.map((cluster) => {
              const {
                properties: { cluster: isCluster },
                geometry: {
                  coordinates: [longitude, latitude],
                },
              } = cluster;

              if (isCluster) {
                return (
                  <LayerGroup
                   key={`clusterx-${cluster.id}`}     
                  >
                  <CircleMarker
                    center={[latitude, longitude]}
                    pathOptions={{ fillColor: clusterSettings.bg_color || 'blue' }}
                    eventHandlers={{
                      click: e => handleClickCluster(cluster)
                    }}
                    radius={20 + (cluster.properties.point_count / points.length) * 3 * 20}
                  />
                  </LayerGroup>
                
                );
              }

              return (
                <Fragment key={`pin-${cluster.properties.uid}`}>
                  <Marker
                    key={`kmarker-${cluster.properties.uid}`}
                    position={[latitude, longitude]}   
                    title={cluster.properties.name}
                    icon={ L.icon({
                      iconUrl: cluster.properties.marker_url || defaultImageUrl,
                      iconSize: [50, 50],
                      className: `osm-marker-icon ${(hoveredLocation?.properties?.uid === cluster.properties.uid || selectedLocation?.properties?.uid === cluster.properties.uid ) ? 'is-hovering' : ''}`        
                  })}                        
                  >
                    <Popup
                      ref={ref => {popupRefs[cluster.properties.uid] = ref; return null;}}
                      id={`popup-${cluster.properties.uid}`}
                      minWidth={isMobile ? 300 : 400}
                    >                  
                     <InfoWindow
                       isVisible={true}
                        onHoverLocation={(e) =>
                          handleHoverLocation(cluster)
                        }
                        selectedLocation={selectedLocation}
                        hoveredLocation={hoveredLocation}
                        item={cluster}
                        onClose={() => console.log("info close")}
                      />
                    </Popup>
                   
                  </Marker>
                </Fragment>
              );
            })}
                       
            <MapEventHooks
              events={{
                resize: handleChangeMapBounds,
                zoom: handleChangeMapBounds,
              }}
            />
          </MapContainer>
        </div>
      </div>
    </div>
  );
};

export default MapBox;
